import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Link as ChakraLink,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import {
  acceptAdminCorrection,
  dismissAdminCorrection,
  getAdminCorrections,
} from "../../../services/adminApi";

const ACTIONS = {
  1: { label: "Add", colorScheme: "green" },
  2: { label: "Edit", colorScheme: "orange" },
  3: { label: "Delete", colorScheme: "red" },
};

const comicLabel = (comic, story) => {
  if (!comic) {
    return "No comic selected";
  }

  return story ? `${comic.title} [${story}]` : comic.title;
};

const ChangeSummary = ({ correction }) => {
  if (correction.action === 1) {
    return (
      <Text>
        Add{" "}
        <strong>{comicLabel(correction.newComic, correction.newStory)}</strong>{" "}
        at position {correction.order}
      </Text>
    );
  }

  if (correction.action === 2) {
    return (
      <Box>
        <Text>
          Replace{" "}
          <Text as="del" color="gray.500">
            {comicLabel(correction.oldComic, correction.oldStory)}
          </Text>{" "}
          with{" "}
          <strong>
            {comicLabel(correction.newComic, correction.newStory)}
          </strong>
        </Text>
        <Text color="gray.500" fontSize="xs">
          Position {correction.order}
        </Text>
      </Box>
    );
  }

  return (
    <Text>
      Remove{" "}
      <Text as="del" color="gray.500">
        {comicLabel(correction.oldComic, correction.oldStory)}
      </Text>{" "}
      at position {correction.order}
    </Text>
  );
};

const submitterLabel = (user) => {
  if (!user) {
    return "Unknown user";
  }

  return user.userHandle
    ? `@${user.userHandle}`
    : user.name || `User #${user.id}`;
};

const submittedLabel = (createdAt) => {
  if (!createdAt) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(createdAt));
};

const Corrections = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef();
  const [correctionToDismiss, setCorrectionToDismiss] = useState(null);
  const correctionsQuery = useQuery(["adminCorrections"], getAdminCorrections, {
    refetchOnWindowFocus: false,
  });

  const handleSuccess = (title, description) => {
    queryClient.invalidateQueries(["adminCorrections"]);
    queryClient.invalidateQueries(["readingOrders"]);
    toast({
      title,
      description,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleError = (title, error) => {
    const status = error.response?.status;
    toast({
      title,
      description:
        error.response?.data?.message ||
        (status ? `Request failed (HTTP ${status}).` : null) ||
        error.message ||
        "Please try again or refresh the queue.",
      status: "error",
      duration: 6000,
      isClosable: true,
    });
  };

  const acceptMutation = useMutation(acceptAdminCorrection, {
    onSuccess: () => {
      handleSuccess(
        "Correction accepted.",
        "The reading order has been updated."
      );
    },
    onError: (error) => handleError("Unable to accept correction.", error),
  });

  const dismissMutation = useMutation(dismissAdminCorrection, {
    onSuccess: () => {
      setCorrectionToDismiss(null);
      handleSuccess(
        "Correction dismissed.",
        "It was removed without changing the reading order."
      );
    },
    onError: (error) => handleError("Unable to dismiss correction.", error),
  });

  if (correctionsQuery.isLoading) {
    return (
      <Flex justify="center" py="12">
        <Spinner />
      </Flex>
    );
  }

  if (correctionsQuery.isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Corrections could not be loaded. Please refresh and try again.
        </AlertDescription>
      </Alert>
    );
  }

  const corrections = correctionsQuery.data?.data || [];

  return (
    <>
      <Box mb="5">
        <Text color="gray.500" fontSize="sm">
          Review the latest user-submitted changes below. Each row is one comic
          change, so a multi-comic suggestion appears as several rows.
        </Text>
      </Box>

      {corrections.length === 0 ? (
        <Alert status="success">
          <AlertIcon />
          <AlertDescription>
            No corrections are waiting for review.
          </AlertDescription>
        </Alert>
      ) : (
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Submitted</Th>
                <Th>Reading order</Th>
                <Th>Submitted by</Th>
                <Th>Change</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {corrections.map((correction) => {
                const action = ACTIONS[correction.action] || {
                  label: "Unknown",
                  colorScheme: "gray",
                };
                const isAccepting =
                  acceptMutation.isLoading &&
                  acceptMutation.variables === correction.id;
                const isDismissing =
                  dismissMutation.isLoading &&
                  dismissMutation.variables === correction.id;

                return (
                  <Tr key={correction.id}>
                    <Td whiteSpace="nowrap">
                      <Text fontSize="sm">
                        {submittedLabel(correction.createdAt)}
                      </Text>
                    </Td>
                    <Td minW="180px">
                      <ChakraLink
                        as={Link}
                        color="blue.300"
                        to={`/reading-orders/${correction.readingOrder?.id}/comics`}
                      >
                        {correction.readingOrder?.title || "Unknown order"}
                      </ChakraLink>
                    </Td>
                    <Td whiteSpace="nowrap">
                      <Text fontSize="sm">
                        {submitterLabel(correction.user)}
                      </Text>
                    </Td>
                    <Td minW="360px">
                      <Flex align="start" gap="2">
                        <Badge colorScheme={action.colorScheme}>
                          {action.label}
                        </Badge>
                        <ChangeSummary correction={correction} />
                      </Flex>
                    </Td>
                    <Td>
                      <Flex gap="2">
                        <Button
                          colorScheme="green"
                          isLoading={isAccepting}
                          loadingText="Accepting"
                          onClick={() => acceptMutation.mutate(correction.id)}
                          size="xs"
                        >
                          Accept
                        </Button>
                        <Button
                          isDisabled={dismissMutation.isLoading}
                          isLoading={isDismissing}
                          loadingText="Dismissing"
                          onClick={() => setCorrectionToDismiss(correction)}
                          size="xs"
                          variant="outline"
                        >
                          Dismiss
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <AlertDialog
        isOpen={!!correctionToDismiss}
        leastDestructiveRef={cancelRef}
        onClose={() => setCorrectionToDismiss(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Dismiss correction?</AlertDialogHeader>
            <AlertDialogBody>
              This removes the suggestion without changing the reading order.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setCorrectionToDismiss(null)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={dismissMutation.isLoading}
                ml="3"
                onClick={() => dismissMutation.mutate(correctionToDismiss.id)}
              >
                Dismiss correction
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Corrections;
